import React, { useState, useEffect } from 'react';
import { FONT_PREVIEW_CHARACTERS } from '../utils';
import { useAssetActions } from '../context/AssetActionsContext';

const weightNames: { [key: string]: string } = {
  ['100']: 'Thin',
  ['200']: 'Extra Light',
  ['300']: 'Light',
  ['400']: 'Regular',
  ['500']: 'Medium',
  ['600']: 'Semi Bold',
  ['700']: 'Bold',
  ['800']: 'Extra Bold',
  ['900']: 'Ultra Bold',
};

const capitalizeFirstLetter = (input: string): string => {
  if (!input) {
    return input;
  }
  return input.charAt(0).toUpperCase() + input.slice(1);
};

const getVariantName = (variant: string, style?: string): string => {
  const weight: string = variant ?? '400';

  return `${weightNames[weight] ?? ''} ${weight}${style ? ` ${capitalizeFirstLetter(style)}` : ''}`;
};

interface FontPreviewProps {
  id: string;
  family: string;
  templateText?: string;
  fontSize?: string;
  isThumbnail?: boolean;
  styles?: string[];
}

export const FontPreview: React.FC<FontPreviewProps> = ({id, family, templateText=FONT_PREVIEW_CHARACTERS, fontSize, isThumbnail, styles = ['normal']}) => {
  const { registry } = useAssetActions();
  const [isLoading, setIsLoading] = useState(true);
  const [variants, setVariants] = useState<string[]>([]);

  useEffect(() => {
    if (!registry) return;

    const loadFont = async () => {
      try {
        const fontsourceProvider = await registry.get('fontsource');
        const fontAsset = await fontsourceProvider?.getAssetById(id);
        const src = fontAsset?.getPreviewUrl();
        const fontVariants = fontAsset?.getMetadata().variants;
        if (fontVariants) {
          setVariants(Object.keys(fontVariants));
        }
        if (src) {
          const face = new FontFace(id, `url(${src})`);
          const loaded = await face.load();
          document.fonts.add(loaded);
        }
        setIsLoading(false);
      } catch (error) {
        console.error(`Error loading font ${id}:`, error);
        setIsLoading(false);
      }
    };

    loadFont();
  }, [id, registry]);

  // NOTE: that FontSource uses ids as font family name in css rather than the actual name of the font (at google fonts it's the family name with + characters instead of spaces)
  const baseStyle = {
    fontFamily: id,
    fontSize: fontSize ?? '16px',
  };

  // const allVersions = variants.length * styles.length;
  // const detailsText = `${allVersions} style${allVersions > 1 ? 's' : ''}`;

  if (isLoading) return (<></>);
  return (
    <div className="font-preview-container">
      {isThumbnail ? (
        <div className="font-thumb-wrapper">
          <div className="font-thumb-title-wrapper">
            <div className="font-thumb-title-container">
              <div className="font-thumb-title">{family}</div>
              {/* <div className="font-thumb-details">{detailsText}</div> */}
            </div>
          </div>
          <p className="font-thumb-preview-wrapper" style={{...baseStyle}}>
            {templateText}
          </p>
        </div>
      ) : (
        <div className="font-preview-wrapper">
          {variants.map(variant => {
            return styles
              .sort(a => (a === 'normal' ? -1 : 0))
              .map(style => {
                const isItalic: boolean = style === 'italic';
                const weight: string = variant ?? '400';

                const fontSizeNumber = fontSize ? parseInt(fontSize) : 8;

                return (
                  <div className="font-preview-size-wrapper" key={`${family}${variant}${style}`}>
                    <div className="font-preview-size-text">{getVariantName(variant, style)}</div>
                    <div
                      className="font-preview-line"
                      style={{...baseStyle, minHeight: fontSizeNumber * 1.5, fontSize: `${fontSize}px`, fontWeight: weight, fontStyle: isItalic ? 'italic' : 'normal', marginBottom: fontSizeNumber > 70 ? 4 : 8}}
                    >
                      {templateText}
                    </div>
                    <div className="font-preview-size-separator" style={{height: `${fontSizeNumber / 2}px`}}></div>
                  </div>
                );
              });
          })}
        </div>
      )}
    </div>
  );
};

